import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, CreditCard, Plus, Trash2, Save, RefreshCw,
  ToggleLeft, ToggleRight, Package, ChevronDown, ChevronRight, Weight, Zap,
  ShoppingCart, Smartphone,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ─────────────────────────────────────────────────────────────────

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
      <Icon className="w-4 h-4 text-orange-500" />
      <div>
        <p className="text-sm font-bold text-slate-700">{title}</p>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border flex-shrink-0 ${
        value
          ? "bg-orange-500 text-white border-orange-500 hover:bg-orange-600"
          : "bg-white text-slate-400 border-slate-300 hover:border-slate-400"
      }`}
    >
      {value ? <><ToggleRight className="w-3.5 h-3.5" /> Enabled</> : <><ToggleLeft className="w-3.5 h-3.5" /> Disabled</>}
    </button>
  );
}

function NumberInput({ value, onChange, min = 0, prefix = "₹", suffix = "" }) {
  return (
    <div className="relative flex items-center">
      {prefix && <span className="absolute left-3 text-sm text-slate-500 pointer-events-none">{prefix}</span>}
      <input
        type="number"
        min={min}
        value={value ?? ""}
        onChange={e => onChange(Number(e.target.value))}
        className={`w-24 ${prefix ? "pl-7" : "pl-3"} pr-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-500 bg-white text-right`}
      />
      {suffix && <span className="absolute right-3 text-xs text-slate-400 pointer-events-none">{suffix}</span>}
    </div>
  );
}

// ── Weight Slabs Section ──────────────────────────────────────────────────────

const DEFAULT_AUTO_CFG = { slab_size_g: 500, base_charge: 70, extra_per_slab: 70, num_slabs: 20 };

function WeightSlabsSection({ slabs, onChange, autoConfig, onAutoConfigChange }) {
  const [showAuto, setShowAuto] = useState(false);
  const [cfg, setCfg] = useState({ ...DEFAULT_AUTO_CFG, ...(autoConfig || {}) });

  // Keep parent's stored config in sync with local state when it loads
  useEffect(() => {
    if (autoConfig) setCfg(c => ({ ...DEFAULT_AUTO_CFG, ...autoConfig, ...c }));
  }, []); // eslint-disable-line

  const update = (id, field, val) =>
    onChange(slabs.map(s => s.id === id ? { ...s, [field]: val } : s));
  const remove = (id) => onChange(slabs.filter(s => s.id !== id));
  const add = () => onChange([
    ...slabs,
    { id: newId(), label: "New Slab", min_g: 0, max_g: null, charge: 0 },
  ]);

  const uc = patch => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    onAutoConfigChange?.(next);
  };

  const formatWeight = g => g >= 1000 ? `${g / 1000}kg` : `${g}g`;

  const generateSlabs = () => {
    const { slab_size_g, base_charge, extra_per_slab, num_slabs } = cfg;
    if (!slab_size_g || slab_size_g <= 0) { return; }
    const generated = Array.from({ length: num_slabs }, (_, i) => {
      const minG = i * slab_size_g;
      const maxG = i < num_slabs - 1 ? (i + 1) * slab_size_g - 1 : null;
      const charge = base_charge + i * extra_per_slab;
      const fromLabel = formatWeight(minG);
      const toLabel = maxG !== null ? formatWeight(maxG + 1) : "∞";
      const label = i === 0
        ? `Up to ${formatWeight(slab_size_g)}`
        : `${fromLabel} – ${toLabel}`;
      return { id: newId(), label, min_g: minG, max_g: maxG, charge };
    });
    onChange(generated);
    onAutoConfigChange?.(cfg);
    toast.success(`Generated ${num_slabs} slabs (${formatWeight(slab_size_g)} × ₹${extra_per_slab})`);
  };

  const preview = Array.from({ length: Math.min(3, cfg.num_slabs) }, (_, i) => ({
    range: i === 0 ? `0–${cfg.slab_size_g - 1}g` : `${i * cfg.slab_size_g}–${(i + 1) * cfg.slab_size_g - 1}g`,
    charge: cfg.base_charge + i * cfg.extra_per_slab,
  }));

  return (
    <div>
      {/* ── Auto-Generate Panel ── */}
      <div className="mx-4 mb-3 border border-orange-200 rounded-xl overflow-hidden bg-orange-50/40">
        <button type="button" onClick={() => setShowAuto(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-orange-50 transition-colors">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-bold text-orange-700">Auto-Generate Slabs</span>
            <span className="text-xs text-orange-500 bg-orange-100 px-2 py-0.5 rounded-full">
              Every {cfg.slab_size_g}g → +₹{cfg.extra_per_slab}
            </span>
          </div>
          {showAuto
            ? <ChevronDown className="w-4 h-4 text-orange-400" />
            : <ChevronRight className="w-4 h-4 text-orange-400" />}
        </button>

        {showAuto && (
          <div className="px-4 pb-4 border-t border-orange-100 bg-white/60">
            <p className="text-xs text-slate-500 mt-3 mb-4">
              Configure the rule once — click <strong>Generate</strong> to auto-build all weight slabs. Existing slabs will be replaced.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {/* Slab size */}
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Slab Size</label>
                <div className="relative">
                  <input type="number" min={100} step={50} value={cfg.slab_size_g}
                    onChange={e => uc({ slab_size_g: Number(e.target.value) })}
                    className="w-full pr-8 pl-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-right"
                    data-testid="auto-slab-size" />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">g</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">grams per slab</p>
              </div>

              {/* Base charge */}
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Base Charge</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-500 pointer-events-none">₹</span>
                  <input type="number" min={0} value={cfg.base_charge}
                    onChange={e => uc({ base_charge: Number(e.target.value) })}
                    className="w-full pl-7 pr-2 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-right"
                    data-testid="auto-base-charge" />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">first slab charge</p>
              </div>

              {/* Extra per slab */}
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Extra per Slab</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-500 pointer-events-none">₹</span>
                  <input type="number" min={0} value={cfg.extra_per_slab}
                    onChange={e => uc({ extra_per_slab: Number(e.target.value) })}
                    className="w-full pl-7 pr-2 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-right"
                    data-testid="auto-extra-per-slab" />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">added each {cfg.slab_size_g}g</p>
              </div>

              {/* Number of slabs */}
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1.5">No. of Slabs</label>
                <input type="number" min={1} max={50} value={cfg.num_slabs}
                  onChange={e => uc({ num_slabs: Math.min(50, Math.max(1, Number(e.target.value))) })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-right"
                  data-testid="auto-num-slabs" />
                <p className="text-[10px] text-slate-400 mt-1">max 50 slabs</p>
              </div>
            </div>

            {/* Live preview */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 mb-3">
              <p className="text-[11px] font-bold text-slate-500 mb-2">Preview (first 3 of {cfg.num_slabs} slabs)</p>
              <div className="flex gap-4">
                {preview.map((p, i) => (
                  <div key={i} className="text-xs text-slate-600">
                    <span className="font-mono text-slate-400">{p.range}</span>
                    <span className="mx-1.5 text-slate-300">→</span>
                    <span className="font-bold text-orange-600">₹{p.charge}</span>
                  </div>
                ))}
                {cfg.num_slabs > 3 && (
                  <div className="text-xs text-slate-400 self-center">
                    … up to ₹{cfg.base_charge + (cfg.num_slabs - 1) * cfg.extra_per_slab} for {formatWeight(cfg.num_slabs * cfg.slab_size_g)}
                  </div>
                )}
              </div>
            </div>

            <button type="button" onClick={generateSlabs}
              data-testid="generate-slabs-btn"
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-5 py-2 rounded-xl transition-colors">
              <Zap className="w-4 h-4" /> Generate {cfg.num_slabs} Slabs
            </button>
          </div>
        )}
      </div>

      {/* ── Manual slabs table ── */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 font-semibold border-b border-slate-100">
              <th className="px-4 py-2.5 text-left w-40">Label</th>
              <th className="px-4 py-2.5 text-left">Min Weight (g)</th>
              <th className="px-4 py-2.5 text-left">Max Weight (g)</th>
              <th className="px-4 py-2.5 text-left">Charge (₹)</th>
              <th className="px-4 py-2.5 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {slabs.map(slab => (
              <tr key={slab.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-2.5">
                  <input type="text" value={slab.label}
                    onChange={e => update(slab.id, "label", e.target.value)}
                    placeholder="e.g. Up to 500g"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500" />
                </td>
                <td className="px-4 py-2.5">
                  <input type="number" min={0} value={slab.min_g ?? ""}
                    onChange={e => update(slab.id, "min_g", Number(e.target.value))}
                    className="w-24 px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-right" />
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <input type="number" min={0} value={slab.max_g ?? ""}
                      onChange={e => update(slab.id, "max_g", e.target.value === "" ? null : Number(e.target.value))}
                      placeholder="∞"
                      className="w-24 px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-right" />
                    <span className="text-xs text-slate-400">blank = no limit</span>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-500">₹</span>
                    <input type="number" min={0} value={slab.charge ?? ""}
                      onChange={e => update(slab.id, "charge", Number(e.target.value))}
                      className="w-24 pl-6 pr-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-right" />
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <button type="button" onClick={() => remove(slab.id)}
                    className="text-slate-400 hover:text-red-500 transition-colors p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 pb-4 pt-2">
        <button type="button" onClick={add}
          className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add Slab
        </button>
      </div>
    </div>
  );
}

// ── Delivery Options Section ──────────────────────────────────────────────────

function DeliveryOptionsSection({ options, onChange }) {
  const update = (id, field, val) =>
    onChange(options.map(o => o.id === id ? { ...o, [field]: val } : o));

  const remove = (id) => onChange(options.filter(o => o.id !== id));

  const add = () => onChange([
    ...options,
    { id: newId(), enabled: true, name: "New Option", description: "", pricing: "flat", flat_charge: 0 },
  ]);

  return (
    <div className="divide-y divide-slate-50">
      {options.map(opt => (
        <div key={opt.id} className="px-5 py-4">
          <div className="flex items-start gap-3 flex-wrap">
            {/* Enable / Disable */}
            <Toggle value={opt.enabled} onChange={v => update(opt.id, "enabled", v)} />

            {/* Name */}
            <input
              type="text"
              value={opt.name}
              onChange={e => update(opt.id, "name", e.target.value)}
              placeholder="Option name"
              className="flex-1 min-w-32 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 font-semibold"
            />

            {/* Description */}
            <input
              type="text"
              value={opt.description}
              onChange={e => update(opt.id, "description", e.target.value)}
              placeholder="e.g. 3–6 business days"
              className="flex-1 min-w-40 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-slate-500"
            />

            {/* Pricing type */}
            <div className="flex items-center gap-2">
              <select
                value={opt.pricing}
                onChange={e => update(opt.id, "pricing", e.target.value)}
                className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 bg-white"
              >
                <option value="weight">Weight-based</option>
                <option value="flat">Flat charge</option>
                <option value="free">Always Free</option>
              </select>
              {opt.pricing === "flat" && (
                <NumberInput
                  value={opt.flat_charge}
                  onChange={v => update(opt.id, "flat_charge", v)}
                  prefix="₹"
                />
              )}
              {opt.pricing === "weight" && (
                <span className="text-xs text-slate-400 italic">uses slabs above</span>
              )}
              {opt.pricing === "free" && (
                <span className="text-xs text-green-600 font-semibold">₹0 always</span>
              )}
            </div>

            <button
              type="button"
              onClick={() => remove(opt.id)}
              className="text-slate-300 hover:text-red-500 transition-colors p-1 ml-auto"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
      <div className="px-5 pb-4 pt-3">
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add Delivery Option
        </button>
      </div>
    </div>
  );
}

// ── Payment Methods Section ───────────────────────────────────────────────────

function PaymentMethodsSection({ methods, onChange }) {
  const update = (id, field, val) =>
    onChange(methods.map(m => m.id === id ? { ...m, [field]: val } : m));

  const remove = (id) => onChange(methods.filter(m => m.id !== id));

  const add = () => onChange([
    ...methods,
    { id: newId(), enabled: true, name: "New Method", description: "", surcharge_type: "none", surcharge_value: 0 },
  ]);

  return (
    <div className="divide-y divide-slate-50">
      {methods.map(m => (
        <div key={m.id} className="px-5 py-4">
          <div className="flex items-start gap-3 flex-wrap">
            <Toggle value={m.enabled} onChange={v => update(m.id, "enabled", v)} />

            <input
              type="text"
              value={m.name}
              onChange={e => update(m.id, "name", e.target.value)}
              placeholder="Method name"
              className="flex-1 min-w-32 px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-semibold focus:outline-none focus:border-orange-500"
            />
            <input
              type="text"
              value={m.description}
              onChange={e => update(m.id, "description", e.target.value)}
              placeholder="Short description"
              className="flex-1 min-w-40 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-500 focus:outline-none focus:border-orange-500"
            />

            {/* Surcharge */}
            <div className="flex items-center gap-2">
              <select
                value={m.surcharge_type}
                onChange={e => update(m.id, "surcharge_type", e.target.value)}
                className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 bg-white"
              >
                <option value="none">No surcharge</option>
                <option value="flat">Flat ₹</option>
                <option value="percent">Percent %</option>
              </select>
              {m.surcharge_type !== "none" && (
                <NumberInput
                  value={m.surcharge_value}
                  onChange={v => update(m.id, "surcharge_value", v)}
                  prefix={m.surcharge_type === "percent" ? "" : "₹"}
                  suffix={m.surcharge_type === "percent" ? "%" : ""}
                />
              )}
            </div>

            <button
              type="button"
              onClick={() => remove(m.id)}
              className="text-slate-300 hover:text-red-500 transition-colors p-1 ml-auto"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
      <div className="px-5 pb-4 pt-3">
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add Payment Method
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CheckoutSettings() {
  const [cs, setCs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState("shipping");

  const token = localStorage.getItem("it_token");

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/checkout-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCs(res.data);
      setDirty(false);
    } catch {
      toast.error("Failed to load checkout settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const mark = (next) => { setCs(next); setDirty(true); };

  const setShipping = (key, val) =>
    mark({ ...cs, shipping: { ...cs.shipping, [key]: val } });

  const setPaymentMethods = (methods) =>
    mark({ ...cs, payment_methods: methods });

  const setCartUi = (patch) =>
    mark({ ...cs, cart_ui: { ...cs.cart_ui, ...patch } });

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/checkout-settings`, cs, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Checkout settings saved");
      setDirty(false);
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
      </div>
    );
  }

  const TABS = [
    { id: "shipping",  label: "Shipping", icon: Truck },
    { id: "payment",   label: "Payment Methods", icon: CreditCard },
    { id: "cartui",    label: "Cart & Checkout UI", icon: ShoppingCart },
  ];

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
            <Package className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Checkout Settings</h1>
            <p className="text-xs text-slate-500">Configure shipping slabs, delivery options and payment methods</p>
          </div>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-600 transition-colors" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-slate-100 p-1 rounded-xl w-fit">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === t.id
                ? "bg-white text-orange-600 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ── Shipping Tab ─────────────────────────────────────────────────── */}
      {tab === "shipping" && cs && (
        <div className="space-y-4">
          {/* Free Threshold */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <SectionHeader icon={Truck} title="Free Shipping Threshold" subtitle="Order totals at or above this amount get free shipping regardless of weight" />
            <div className="px-5 py-4 flex items-center gap-4">
              <span className="text-sm text-slate-600">Free shipping when order total ≥</span>
              <NumberInput
                value={cs.shipping.free_threshold}
                onChange={v => setShipping("free_threshold", v)}
                prefix="₹"
              />
              <span className="text-xs text-slate-400">(set 0 to never give free shipping)</span>
            </div>
          </div>

          {/* Dispatch time texts (for products that continue selling when out of stock) */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <SectionHeader icon={Truck} title="Dispatch Time Texts" subtitle="Shown under each variant in the cart and checkout. Used for products set to 'Continue selling when out of stock'." />
            <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-xs font-semibold text-slate-600 mb-1">In-stock variant</span>
                <input data-testid="dispatch-in-stock" value={cs.shipping.dispatch_in_stock_text ?? "Dispatch within 1 day"} onChange={e => setShipping("dispatch_in_stock_text", e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400" />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-600 mb-1">Out-of-stock variant</span>
                <input data-testid="dispatch-out-of-stock" value={cs.shipping.dispatch_out_of_stock_text ?? "Dispatch within 10 days"} onChange={e => setShipping("dispatch_out_of_stock_text", e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400" />
              </label>
            </div>
          </div>

          {/* Weight Slabs */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <SectionHeader
              icon={Weight}
              title="Weight-Based Shipping Slabs"
              subtitle="Charge is determined by each shipment's weight (sum of qty × variant weight). Used by 'weight-based' delivery options. Orders with out-of-stock items ship in separate shipments, each charged by its own weight."
            />
            <WeightSlabsSection
              slabs={cs.shipping.weight_slabs || []}
              onChange={v => setShipping("weight_slabs", v)}
              autoConfig={cs.shipping.weight_slab_auto_config || {}}
              onAutoConfigChange={v => setShipping("weight_slab_auto_config", v)}
            />
            <div className="px-5 py-4 border-t border-slate-100 flex flex-wrap items-center gap-3" data-testid="extra-above-last-slab">
              <span className="text-sm text-slate-600">Above the heaviest slab, add</span>
              <NumberInput
                value={cs.shipping.extra_above_last_slab?.charge ?? 70}
                onChange={v => setShipping("extra_above_last_slab", { step_g: 500, ...(cs.shipping.extra_above_last_slab || {}), charge: v })}
              />
              <span className="text-sm text-slate-600">for every</span>
              <NumberInput
                prefix=""
                value={cs.shipping.extra_above_last_slab?.step_g ?? 500}
                onChange={v => setShipping("extra_above_last_slab", { ...(cs.shipping.extra_above_last_slab || {}), step_g: v })}
              />
              <span className="text-sm text-slate-600">g</span>
              <span className="text-xs text-slate-400">(set ₹0 to charge the heaviest slab's amount for anything heavier)</span>
            </div>
          </div>

          {/* Delivery Options */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <SectionHeader
              icon={Truck}
              title="Delivery Options"
              subtitle="Options shown at checkout. Weight-based options use the slabs above. Flat-charge options use a fixed fee."
            />
            <DeliveryOptionsSection
              options={cs.shipping.delivery_options || []}
              onChange={v => setShipping("delivery_options", v)}
            />
          </div>
        </div>
      )}

      {/* ── Payment Tab ──────────────────────────────────────────────────── */}
      {tab === "payment" && cs && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <SectionHeader
              icon={CreditCard}
              title="Payment Methods"
              subtitle="Control which payment methods appear at checkout. Add a surcharge for COD or any method."
            />
            <PaymentMethodsSection
              methods={cs.payment_methods || []}
              onChange={setPaymentMethods}
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
            <strong>Note:</strong> The <em>Online Payment</em> method uses Razorpay (UPI, Card, Net Banking). Disabling it removes those options at checkout. COD and custom methods are placed as offline orders.
          </div>
        </div>
      )}

      {/* ── Cart UI Tab ──────────────────────────────────────────────────── */}
      {tab === "cartui" && cs && (() => {
        const ui = cs.cart_ui || {};
        return (
          <div className="space-y-4">
            {/* Delivery days */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <SectionHeader icon={Truck} title="Estimated Delivery Days" subtitle='Shown on cart and checkout as "Delivery by Mon, 5 Aug"' />
              <div className="px-5 py-4 flex items-center gap-4">
                <span className="text-sm text-slate-600">Days from today</span>
                <input
                  type="number" min={1} max={30}
                  value={ui.delivery_days ?? 3}
                  onChange={e => setCartUi({ delivery_days: Number(e.target.value) })}
                  className="w-20 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-center"
                  data-testid="cart-delivery-days-input"
                />
                <span className="text-xs text-slate-400">
                  E.g. 3 → "Delivery by {new Date(Date.now() + (ui.delivery_days ?? 3) * 86400000).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}"
                </span>
              </div>
            </div>

            {/* Savings zone */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <SectionHeader icon={Smartphone} title="Savings Zone (Coupon Banner)" subtitle="Show the WOW! savings zone bar on the mobile cart page" />
              <div className="px-5 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Show Savings Zone</p>
                  <p className="text-xs text-slate-400 mt-0.5">Displays a "View all coupons" row below cart items on mobile</p>
                </div>
                <Toggle
                  value={ui.show_savings_zone ?? true}
                  onChange={v => setCartUi({ show_savings_zone: v })}
                />
              </div>
            </div>

            {/* CTA text */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <SectionHeader icon={ShoppingCart} title="Call-to-Action Button Text" subtitle="Labels for the primary action buttons on mobile cart and checkout" />
              <div className="px-5 py-4 space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm text-slate-600 w-44 flex-shrink-0">Cart page CTA</label>
                  <input
                    type="text"
                    value={ui.cart_cta_text ?? "Proceed to buy"}
                    onChange={e => setCartUi({ cart_cta_text: e.target.value })}
                    placeholder="Proceed to buy"
                    className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-500"
                    data-testid="cart-cta-text-input"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-sm text-slate-600 w-44 flex-shrink-0">Checkout step CTA</label>
                  <input
                    type="text"
                    value={ui.checkout_cta_text ?? "Continue"}
                    onChange={e => setCartUi({ checkout_cta_text: e.target.value })}
                    placeholder="Continue"
                    className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-500"
                    data-testid="checkout-cta-text-input"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Save */}
      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          data-testid="checkout-settings-save"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            dirty
              ? "bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          {saving
            ? <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {dirty && <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>}
      </div>
    </div>
  );
}
